import { AVAILABLE_LOCALES } from './i18n/types';
import type { Locale } from './i18n/types';
/**
 * Public config polling (kill-switches, maintenance banner, disabled countries).
 *
 * Backend: GET /api/config returns { paymentsEnabled, registrationsEnabled,
 * maintenanceBanner, maintenanceHard, disabledCountries }.
 * Server caches 5s; we poll every 60s and notify subscribers on change.
 *
 * Subscribers can react in real time — main.ts re-renders the maintenance
 * banner + disables Top-up CTAs when paymentsEnabled flips.
 */

export interface BannerConfig {
  enabled: boolean;
  severity: 'promo' | 'maintenance' | 'critical';
  /** Who sees it. Resolved server-side from the admin choice; when an older
   *  backend sends nothing, promos go to guests and notices to everyone. */
  audience: 'all' | 'guests';
  text: Record<Locale, string>;
}

export interface PublicConfig {
  paymentsEnabled: boolean;
  registrationsEnabled: boolean;
  maintenanceBanner: string;
  maintenanceHard: boolean;
  disabledCountries: string[];
  banner: BannerConfig;
  /** Free-trial eSIM (mig 0054): the dashboard shows the trial CTA only when enabled. */
  trial: { enabled: boolean; creditMicroUsdt: number };
  /** Web Push VAPID public key. Empty if push not yet initialized
   *  server-side. Frontend treats empty as "push unavailable, hide UI". */
  pushVapidKey: string;
}

const DEFAULT_BANNER: BannerConfig = {
  enabled: false,
  severity: 'promo',
  audience: 'guests',
  text: Object.fromEntries(AVAILABLE_LOCALES.map((l) => [l, ''])) as Record<Locale, string>,
};

const DEFAULT_CONFIG: PublicConfig = {
  paymentsEnabled: true,
  registrationsEnabled: true,
  maintenanceBanner: '',
  maintenanceHard: false,
  disabledCountries: [],
  banner: DEFAULT_BANNER,
  trial: { enabled: false, creditMicroUsdt: 0 },
  pushVapidKey: '',
};

const POLL_INTERVAL_MS = 60_000;

let current: PublicConfig = DEFAULT_CONFIG;
let primed = false;
const listeners = new Set<(c: PublicConfig) => void>();

function notify(): void {
  for (const fn of listeners) {
    try { fn(current); } catch { /* one bad listener shouldn't kill the others */ }
  }
}

function shallowDifferent(a: PublicConfig, b: PublicConfig): boolean {
  if (a.paymentsEnabled !== b.paymentsEnabled) return true;
  if (a.registrationsEnabled !== b.registrationsEnabled) return true;
  if (a.maintenanceBanner !== b.maintenanceBanner) return true;
  if (a.maintenanceHard !== b.maintenanceHard) return true;
  if (a.trial.enabled !== b.trial.enabled || a.trial.creditMicroUsdt !== b.trial.creditMicroUsdt) return true;
  if (a.disabledCountries.length !== b.disabledCountries.length) return true;
  for (let i = 0; i < a.disabledCountries.length; i++) {
    if (a.disabledCountries[i] !== b.disabledCountries[i]) return true;
  }
  if (a.banner.enabled !== b.banner.enabled) return true;
  if (a.banner.severity !== b.banner.severity) return true;
  if (a.banner.audience !== b.banner.audience) return true;
  for (const k of AVAILABLE_LOCALES) {
    if (a.banner.text[k] !== b.banner.text[k]) return true;
  }
  return false;
}

async function fetchOnce(): Promise<void> {
  try {
    const res = await fetch('/api/config', { credentials: 'include' });
    if (!res.ok) return;
    const data = (await res.json()) as PublicConfig;
    if (!data || typeof data !== 'object') return;
    // Defensive normalisation: an older backend that doesn't yet ship the
    // banner field gives us `undefined` here, and we fall back to the
    // default. Same for missing per-locale strings.
    const rawBanner = (data as { banner?: Partial<BannerConfig> }).banner ?? {};
    const txt = (rawBanner.text ?? {}) as Partial<BannerConfig['text']>;
    const severity: BannerConfig['severity'] = rawBanner.severity === 'maintenance' || rawBanner.severity === 'critical'
      ? rawBanner.severity
      : 'promo';
    const next: PublicConfig = {
      paymentsEnabled: !!data.paymentsEnabled,
      registrationsEnabled: !!data.registrationsEnabled,
      maintenanceBanner: typeof data.maintenanceBanner === 'string' ? data.maintenanceBanner : '',
      maintenanceHard: !!data.maintenanceHard,
      disabledCountries: Array.isArray(data.disabledCountries) ? data.disabledCountries : [],
      banner: {
        enabled: !!rawBanner.enabled,
        severity,
        audience: rawBanner.audience === 'all' || rawBanner.audience === 'guests'
          ? rawBanner.audience
          : severity === 'promo' ? 'guests' : 'all',
        // Built from the shared list: a locale the API starts sending must not
        // need an edit here to reach the screen.
        text: {
          ...(Object.fromEntries(
            AVAILABLE_LOCALES.map((l) => [l, typeof txt[l] === 'string' ? (txt[l] as string) : '']),
          ) as Record<Locale, string>),
        },
      },
      trial: {
        enabled: !!(data.trial && (data.trial as { enabled?: unknown }).enabled),
        creditMicroUsdt: Number((data.trial as { creditMicroUsdt?: unknown } | undefined)?.creditMicroUsdt ?? 0) || 0,
      },
      pushVapidKey: typeof (data as { pushVapidKey?: unknown }).pushVapidKey === 'string'
        ? (data as { pushVapidKey: string }).pushVapidKey
        : '',
    };
    primed = true;
    if (shallowDifferent(current, next)) {
      current = next;
      notify();
    }
  } catch {
    /* offline / backend not running — keep current value */
  }
}

let pollHandle: ReturnType<typeof setInterval> | null = null;

/**
 * Boot the polling loop. Safe to call multiple times — second call is a no-op.
 * First fetch runs immediately, then every 60s.
 */
export function startConfigPoll(): void {
  if (pollHandle) return;
  void fetchOnce();
  pollHandle = setInterval(() => { void fetchOnce(); }, POLL_INTERVAL_MS);
  if (typeof pollHandle.unref === 'function') (pollHandle as unknown as { unref: () => void }).unref();
}

/** Current cached config. May be the default until first fetch resolves. */
export function getConfig(): PublicConfig {
  return current;
}

/** True once the first /api/config response has landed. */
export function isPrimed(): boolean {
  return primed;
}

/**
 * Subscribe to config changes. Called once immediately with current value
 * (so subscribers can render straight away and don't need to remember to
 * pull-once-then-subscribe). Returns an unsubscribe handle.
 */
export function onConfigChange(fn: (c: PublicConfig) => void): () => void {
  listeners.add(fn);
  fn(current);
  return () => { listeners.delete(fn); };
}
